<template>
  <div class="funeraria-dashboard">
    <div class="header">
      <h2>{{ greetingMessage }}</h2>
      <el-button @click="userStore.logout()" type="primary">Logout</el-button>
    </div>
    <el-alert
      title="ATENÇÃO: Ao clicar em uma vaga disponível, ela será reservada temporariamente para sua funerária por 10 (dez) minutos. A falha em concluir a requisição dentro deste tempo resultará em um bloqueio temporário de reservas para a sua funerária por 01 (uma) hora. A reserva agora também exige confirmação. NÃO clique em vagas se não tiver certeza da escolha. O abuso deste recurso poderá resultar em penalizações mais severas."
      type="warning"
      show-icon
      style="margin-bottom: 16px;"
    />

    <el-tabs v-model="activeTab">
      <!-- Vagas Disponíveis Tab -->
      <el-tab-pane label="Vagas Disponíveis" name="vagasDisponiveis">
        <div class="tab-content">
          <h3>Vagas Disponíveis</h3>
          <el-input v-model="vagasDisponiveisFilter" placeholder="Filtrar vagas..." style="margin: 10px 0; max-width: 300px;" />
          <!-- Add legend above the table -->
          <el-row gutter="12" style="margin-bottom: 10px; align-items: center;">
            <el-col :span="24">
              <div class="vagas-warning" style="margin-bottom: 10px; color: #e6a23c; font-weight: bold; display: flex; align-items: center; gap: 16px;">
                <span>
                  Se você encontrar problemas ao criar uma requisição, clique no botão de atualizar. As reservas de vagas duram apenas 10 minutos e, após esse tempo, a vaga volta a ficar disponível para todos.
                </span>
                <el-button
                  type="warning"
                  size="small"
                  :disabled="refreshCooldown > 0 || loadingVagasDisponiveis"
                  @click="handleRefreshVagas"
                >
                  Atualizar vagas
                  <template v-if="refreshCooldown > 0"> ({{ refreshCooldown }})</template>
                </el-button>
              </div>
            </el-col>
            <el-col :span="12">
              <el-tag type="success" effect="plain"><el-icon><i class="el-icon-circle-check" /></el-icon> Disponível</el-tag>
            </el-col>
            <el-col :span="12">
              <el-tag type="warning" effect="plain"><el-icon><i class="el-icon-user" /></el-icon> Reservada por você</el-tag>
            </el-col>
          </el-row>
          <el-table :data="filteredVagasDisponiveis" v-loading="loadingVagasDisponiveis" style="margin-top: 20px; width: 100%">
            <el-table-column prop="quadra" label="Quadra" />
            <el-table-column prop="numeroSepultura" label="Nº Sepultura" />
            <el-table-column label="Tipo">
              <template #default="{ row }">
                <span>
                  {{ row.tipo || 'N/A' }}
                  <template v-if="row.obeso">
                    (A:{{ row.altura }} L:{{ row.largura }})
                  </template>
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Cemitério">
              <template #default="{ row }">
                {{ row.cemiterio ? row.cemiterio.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column label="Status" width="160">
              <template #default="{ row }">
                <el-tag
                  v-if="reservedVagas[row.id]"
                  type="warning"
                  effect="plain"
                >
                  <el-icon><i class="el-icon-user" /></el-icon> Reservada por você
                </el-tag>
                <el-tag
                  v-else
                  type="success"
                  effect="plain"
                >
                  <el-icon><i class="el-icon-circle-check" /></el-icon> Disponível
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Ações" width="180">
              <template #default="{ row }">
                <el-button
                  size="small"
                  @click="selectVaga(row)"
                  type="primary"
                >
                  Selecionar
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>

      <!-- Criar Requisição Tab -->
      <el-tab-pane label="Criar Requisição" name="criarRequisicao" :disabled="!hasActiveReservation">
        <div class="tab-content">
          <h3>Criar Requisição</h3>
          
          <!-- Mensagem quando não há vaga selecionada -->
          <div v-if="!hasActiveReservation" class="no-reservation-message">
            <el-alert
              title="Nenhuma vaga selecionada"
              description="Para criar uma requisição, primeiro selecione uma vaga disponível na aba 'Vagas Disponíveis'."
              type="warning"
              show-icon
              :closable="false"
            />
          </div>

          <!-- Conteúdo da aba quando há vaga selecionada -->
          <div v-else>
            <div v-if="reservationTimer > 0" class="reservation-timer">
              Tempo restante para completar a requisição: <b>{{ formattedReservationTimer }}</b>
            </div>
            <div v-else class="reservation-timer-expired">
              Reserva expirada. Redirecionando...
            </div>
            <el-form :model="newRequisicao" :rules="requisicaoRules" ref="requisicaoFormRef" label-position="top">
            <el-form-item label="Nome do Falecido" prop="nomeFalecido">
              <el-input v-model="newRequisicao.nomeFalecido" />
            </el-form-item>
              <el-form-item label="Tipo de Documento" prop="tipoDocumento">
                <el-select v-model="newRequisicao.tipoDocumento">
                  <el-option label="CPF" value="CPF" />
                  <el-option label="RG" value="RG" />
                  <el-option label="CNH" value="CNH" />
                  <el-option label="CTPS" value="CTPS" />
                  <el-option label="TSE" value="TSE" />
                  <el-option label="Passaporte" value="PASSAPORTE" />
                </el-select>
              </el-form-item>
            <el-form-item label="Documento do Falecido" prop="cpfFalecido">
              <el-input v-model="newRequisicao.cpfFalecido"/>
            </el-form-item>
            <el-form-item label="Data de Falecimento" prop="dataFalecimento">
              <el-date-picker
                  v-model="newRequisicao.dataFalecimento"
                  type="date"
                  placeholder="Selecione a data"
              />
            </el-form-item>
            <el-form-item label="Falecido Obeso?" prop="obeso" required>
              <el-radio-group v-model="newRequisicao.obeso">
                <el-radio :value="true">Sim</el-radio>
                <el-radio :value="false">Não</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="Vaga Selecionada" prop="vagaId">
              <el-input
                  v-model="newRequisicao.vagaId"
                  placeholder="Escolha uma vaga na aba 'Vagas Disponíveis'"
                  disabled
              />
            </el-form-item>
            <!-- Data e Hora da Requisição -->
            <el-form-item label="Data da Requisição" prop="dataRequisicao">
              <el-date-picker
                  v-model="newRequisicao.dataRequisicao"
                  type="date"
                  placeholder="Selecione a data"
                  :disabled-date="disabledRequisicaoDate"
                  @change="onDateChange"
              />
            </el-form-item>
            <el-form-item label="Hora da Requisição" prop="horaRequisicao">
              <el-select
                v-model="newRequisicao.horaRequisicao"
                :placeholder="loadingTimeSlots ? 'Carregando Horários...' : 'Selecione o horário'"
                :disabled="!newRequisicao.dataRequisicao"
                v-loading="loadingTimeSlots"
                >
              <el-option
                v-if="loadingTimeSlots"
                label="Carregando Horários..."
                value=""
                disabled
                />
              <el-option
                v-else
                v-for="item in availableTimeSlots"
                :key="item.value"
                :label="item.value"
                :value="item.value"
                :disabled="item.disabled"
                />
               </el-select>
            </el-form-item>
            <!-- Upload de Arquivos com indicadores de status -->
            <el-form-item label="Arquivos (Enviar RG e DO no formato .pdf, .jpeg, .png com tamanho máximo de 5mb)">
              <div class="upload-container">
                <el-upload
                    ref="rgUploadRef"
                    action=""
                    :show-file-list="false"
                    :limit="1"
                    :auto-upload="false"
                    :accept="allowedTypes.join(',')"
                    :before-upload="beforeUpload"
                    @change="onRGFileChange"
                    @remove="onRGFileRemove"
                >
                  <el-button type="primary">Adicionar RG</el-button>
                </el-upload>
                <span class="file-status">
                  {{ rgFileName }}
                  <el-icon @click="onRGFileRemove" style="cursor: pointer; margin-left: 4px;">
                    <Close />
                  </el-icon>
                </span>
                <el-upload
                    ref="doUploadRef"
                    action=""
                    :show-file-list="false"
                    :limit="1"
                    :auto-upload="false"
                    :accept="allowedTypes.join(',')"
                    :before-upload="beforeUpload"
                    @change="onDOFileChange"
                    @remove="onDOFileRemove"
                >
                  <el-button type="primary">Adicionar DO</el-button>
                </el-upload>
                <span class="file-status">
                  {{ doFileName }}
                  <el-icon @click="onDOFileRemove" style="cursor: pointer; margin-left: 4px;">
                    <Close />
                  </el-icon>
                </span>
              </div>
              <template #tip>
                <div class="el-upload-tip">Apenas arquivos(.pdf, .jpeg, .png, .gif, .bmp, .docx, .odt) com tamanho máximo de 5MB.</div>
              </template>
            </el-form-item>
            <el-form-item label="ID da DO (Documento de Óbito)" prop="doId" required>
              <el-input
                  v-model="newRequisicao.doId"
                  @input="applyDOMask"
                  placeholder="Informe o ID do Documento de Óbito"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="confirmCreateRequisicao">
                Criar Requisição
              </el-button>
            </el-form-item>
          </el-form>
          </div>
        </div>
      </el-tab-pane>

      <!-- Minhas Requisições Tab -->
      <el-tab-pane label="Minhas Requisições" name="minhasRequisicoes">
        <div class="tab-content">
          <h3>Minhas Requisições</h3>
          <el-input v-model="minhasRequisicoesFilter" placeholder="Filtrar requisições..." style="margin: 10px 0; max-width: 300px;" />
          <el-table
            :data="filteredMinhasRequisicoes"
            v-loading="loadingMinhasRequisicoes"
            style="margin-top: 20px; width: 100%"
            table-layout="auto"
          >
            <el-table-column prop="nomeFalecido" label="Falecido" min-width="160" show-overflow-tooltip />
            <el-table-column label="Obeso?" min-width="80">
              <template #default="{ row }">{{ row.obeso ? 'Sim' : 'Não' }}</template>
            </el-table-column>
            <el-table-column prop="doId" label="ID da DO" min-width="120" show-overflow-tooltip />
            <el-table-column label="Status" :formatter="formatStatus" min-width="110" />
            <el-table-column label="Vaga (Quadra - Sepultura - Tipo)" :formatter="formatVaga" min-width="200" show-overflow-tooltip />
            <el-table-column label="Cemitério" min-width="140" show-overflow-tooltip>
              <template #default="{ row }">
                {{ row.vaga && row.vaga.cemiterio ? row.vaga.cemiterio.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column prop="criadoEm" label="Criado Em" :formatter="formatCriadoEm" min-width="150" />
            <el-table-column prop="dataRequisicao" label="Data da Requisição" min-width="130" />
            <el-table-column prop="horaRequisicao" label="Hora" min-width="80" />
            <el-table-column prop="reason" label="Motivo" min-width="120" show-overflow-tooltip />
            <el-table-column v-if="false" label="Comprovante de Pagamento" min-width="180" fixed="right">
              <template #default="{ row }">
                <a v-if="hasComprovante(row)" href="#" @click.prevent="handleDownloadComprovante(row.id)">
                  Ver comprovante do pagamento
                </a>
                <el-button
                  v-else
                  size="small"
                  type="primary"
                  @click="openComprovanteDialog(row)"
                >
                  Anexar comprovante do pagamento
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>

      <!-- Requisições em andamento Tab -->
      <el-tab-pane label="Requisições em andamento" name="requisicoesAndamento">
        <div class="tab-content">
          <h3>Requisições em andamento</h3>
          <el-input v-model="requisicoesAndamentoFilter" placeholder="Filtrar requisições..." style="margin: 10px 0; max-width: 300px;" />
          <el-table :data="filteredRequisicoesAndamento" v-loading="loadingRequisicoesAndamento" style="margin-top: 20px; width: 100%">
            <el-table-column label="Falecido">
              <template #default="{ row }">
                {{ row.nomeFalecido }} ({{ row.tipoDocumento }}: {{ row.cpfFalecido }})
              </template>
            </el-table-column>
            <el-table-column label="Obeso?" width="90">
              <template #default="{ row }">{{ row.obeso ? 'Sim' : 'Não' }}</template>
            </el-table-column>
            <el-table-column prop="status" label="Status" />
            <el-table-column label="Vaga (Quadra - Sepultura)" :formatter="formatVaga" />
            <el-table-column label="Cemitério">
              <template #default="{ row }">
                {{ row.vaga && row.vaga.cemiterio ? row.vaga.cemiterio.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column label="Funeraria">
              <template #default="{ row }">
                {{ row.funeraria ? row.funeraria.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column prop="criadoEm" label="Criado Em" />
          </el-table>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- Comprovante and Extra Files Dialog -->
    <el-dialog v-model="comprovanteDialogVisible" title="Anexar Comprovante e Arquivos Extras" width="600px">
      <el-form label-position="top">
        <el-form-item label="Comprovante de Pagamento (Obrigatório, máx 5MB)" required>
          <div class="upload-container">
            <el-upload
              ref="comprovanteUploadRef"
              action=""
              :show-file-list="false"
              :limit="1"
              :auto-upload="false"
              :accept="allowedTypes.join(',')"
              :before-upload="beforeUpload"
              @change="onComprovanteChange"
              @remove="onComprovanteRemove"
            >
              <el-button type="primary">Selecionar Comprovante</el-button>
            </el-upload>
            <span class="file-status" v-if="comprovanteFile">
              {{ comprovanteFile.name }}
              <el-icon @click="onComprovanteRemove" style="cursor: pointer; margin-left: 4px;"><Close /></el-icon>
            </span>
          </div>
        </el-form-item>
        
        <el-form-item label="Arquivos Extras (Opcional, até 4 arquivos)">
          <el-upload
            ref="extraFilesUploadRef"
            action=""
            :auto-upload="false"
            :limit="4"
            :multiple="true"
            :accept="allowedTypes.join(',')"
            v-model:file-list="extraFileList"
            :on-exceed="handleExtraFilesExceed"
          >
            <el-button>Adicionar Arquivos</el-button>
            <template #tip>
              <div class="el-upload__tip">Até 4 arquivos. Os nomes originais serão preservados.</div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="comprovanteDialogVisible = false">Cancelar</el-button>
        <el-button type="primary" :loading="comprovanteSubmitting" @click="submitComprovanteUpload">Enviar</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, onUnmounted, computed } from 'vue';
import apiClient from '@/plugins/axios';
import { ElMessage } from 'element-plus';
import { useUserStore } from '@/stores/user';
import { Close } from '@element-plus/icons-vue';

const getErrorMessage = (error) => {
  if (error.response && error.response.data && error.response.data.message) {
    if (Array.isArray(error.response.data.message)) {
      return error.response.data.message.join(', ');
    }
    return error.response.data.message;
  }
  return 'Ocorreu um erro desconhecido. Tente novamente.';
};

const userStore = useUserStore();
const activeTab = ref('vagasDisponiveis');

// Vagas Disponíveis
const vagasDisponiveis = ref([]);
const loadingVagasDisponiveis = ref(false);
const fetchVagasDisponiveis = async () => {
  loadingVagasDisponiveis.value = true;
  try {
    const { data } = await apiClient.get('/vagas/funeraria/disponiveis');
    vagasDisponiveis.value = data;
  } catch (error) {
    ElMessage.error(getErrorMessage(error));
    console.error('Erro ao buscar vagas disponíveis', error);
  } finally {
    loadingVagasDisponiveis.value = false;
  }
};

const reservedVagas = ref({}); // { [vagaId]: true }

const RESERVATION_DURATION = 10 * 60; // 10 minutes in seconds
const reservationTimer = ref(0);
let reservationInterval = null;

const formattedReservationTimer = computed(() => {
  const min = Math.floor(reservationTimer.value / 60).toString().padStart(2, '0');
  const sec = (reservationTimer.value % 60).toString().padStart(2, '0');
  return `${min}:${sec}`;
});

const startReservationTimer = () => {
  reservationTimer.value = RESERVATION_DURATION;
  if (reservationInterval) clearInterval(reservationInterval);
  reservationInterval = setInterval(() => {
    if (reservationTimer.value > 0) {
      reservationTimer.value--;
    } else {
      clearInterval(reservationInterval);
      handleReservationExpired();
    }
  }, 1000);
};

const handleReservationExpired = () => {
  Object.assign(newRequisicao.value, {
    nomeFalecido: '',
    tipoDocumento: '',
    cpfFalecido: '',
    dataFalecimento: null,
    vagaId: '',
    dataRequisicao: null,
    horaRequisicao: '',
    reason: ''
  });
  clearFiles();
  ElMessage.warning('O tempo da reserva (10 minutos) expirou. Sua funerária ficará bloqueada por 1 hora para novas reservas. Por favor, selecione uma vaga novamente após este período.');
  activeTab.value = 'vagasDisponiveis';
};

// Start timer when a vaga is reserved
const selectVaga = async (vaga) => {
  // Mensagem de confirmação antes de reservar
  const confirmationMessage = `Você está prestes a RESERVAR a sepultura\n\nQuadra: ${vaga.quadra}\nNº Sepultura: ${vaga.numeroSepultura}\nTipo: ${vaga.tipo || 'N/A'}\nCemitério: ${vaga.cemiterio ? vaga.cemiterio.nome : 'N/A'}\n\npara a sua funerária por 10 (dez) minutos. A falha em concluir a requisição dentro deste tempo resultará em um bloqueio temporário de reservas de quaisquer vagas para a sua funerária por 01 (uma) hora. Deseja prosseguir?`;
  
  if (!confirm(confirmationMessage)) {
    return;
  }

  try {
    await apiClient.post(`/vagas/${vaga.id}/reserve`);
    reservedVagas.value[vaga.id] = true;
    newRequisicao.value.vagaId = vaga.id;
    ElMessage.success(`Vaga reservada: Quadra ${vaga.quadra}, Nº ${vaga.numeroSepultura}`);
    activeTab.value = 'criarRequisicao';
    startReservationTimer();
  } catch (error) {
    if (error.response && error.response.status === 409) {
      ElMessage.error(getErrorMessage(error));
      fetchVagasDisponiveis(); // Refresh the vacancy list
    } else {
      ElMessage.error(getErrorMessage(error));
    }
  }
};

// Criar Requisição
const newRequisicao = ref({
  nomeFalecido: '',
  tipoDocumento: '',
  cpfFalecido: '',
  dataFalecimento: null,
  vagaId: '',
  dataRequisicao: null,
  horaRequisicao: '',
  reason: '',
  obeso: null,
  doId: '',
});

const applyDOMask = (val) => {
  if (!val) return;
  newRequisicao.value.doId = val.replace(/\D/g, '');
};

const requisicaoRules = {
  nomeFalecido: [{ required: true, message: 'Nome do falecido é obrigatório', trigger: 'blur' }],
  cpfFalecido: [{ required: true, message: 'Documento do falecido é obrigatório', trigger: 'blur' }],
  dataFalecimento: [{ required: true, message: 'Data de falecimento é obrigatória', trigger: 'change' }],
  vagaId: [{ required: true, message: 'Selecione uma vaga', trigger: 'change' }],
  dataRequisicao: [{ required: true, message: 'Data da requisição é obrigatória', trigger: 'change' }],
  horaRequisicao: [{ required: true, message: 'Hora da requisição é obrigatória', trigger: 'change' }],
  obeso: [{
    validator: (rule, value, callback) =>
      (value === true || value === false)
        ? callback()
        : callback(new Error('Informe se o falecido é obeso')),
    trigger: 'change'
  }],
  doId: [{ required: true, message: 'ID da DO é obrigatório', trigger: 'blur' }]
};

const rgFile = ref(null);
const doFile = ref(null);
const rgFileName = ref('Nenhum arquivo selecionado');
const doFileName = ref('Nenhum arquivo selecionado');

const updateFileNames = () => {
  rgFileName.value = rgFile.value ? rgFile.value.name : 'Nenhum arquivo selecionado';
  doFileName.value = doFile.value ? doFile.value.name : 'Nenhum arquivo selecionado';
};

const clearFiles = () => {
  rgFile.value = null;
  doFile.value = null;
  updateFileNames();
  if (rgUploadRef.value) rgUploadRef.value.clearFiles();
  if (doUploadRef.value) doUploadRef.value.clearFiles();
};

const allowedTypes = [
  // documentos
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/rtf',
  'text/plain',
  'text/csv',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  // imagens
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/webp',
  'image/svg+xml',
  'image/tiff',
  'image/x-icon',
]

const beforeUpload = (file) => {
  const allowedTypes = [
    // documentos
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/rtf',
    'text/plain',
    'text/csv',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    // imagens
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/webp',
    'image/svg+xml',
    'image/tiff',
    'image/x-icon',
  ]
  const isAllowedType = allowedTypes.includes(file.type)
  const isLt5MB = file.size / 1024 / 1024 < 5;
  if (!isAllowedType) {
    ElMessage.error('Somente documentos / fotos são permitidos.');
  }
  if (!isLt5MB) {
    ElMessage.error('O tamanho do arquivo não pode exceder 5MB.');
  }
  if (!(isAllowedType && isLt5MB)) {
    if (rgFile.value) {
      rgFile.value = null;
    }
  }
  return isAllowedType && isLt5MB;
};

const formatCriadoEm = (row, column, cellValue) => {
  if (!cellValue) return 'N/A';
  return new Date(cellValue).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const onRGFileChange = (file, fileListVal) => {
  if (fileListVal && fileListVal.length > 0) {
    rgFile.value = fileListVal[0];
    updateFileNames();
  }
};
const onDOFileChange = (file, fileListVal) => {
  if (fileListVal && fileListVal.length > 0) {
    doFile.value = fileListVal[0];
    updateFileNames();
  }
};

const onRGFileRemove = () => {
  rgFile.value = null;
  updateFileNames();
  if (rgUploadRef.value) {
    rgUploadRef.value.clearFiles();
  }
};
const onDOFileRemove = () => {
  doFile.value = null;
  updateFileNames();
  if (doUploadRef.value) {
    doUploadRef.value.clearFiles();
  }
};
const requisicaoFormRef = ref(null);
const rgUploadRef = ref(null);
const doUploadRef = ref(null);

const formatDateBRA = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const availableTimeSlots = ref([]);
const loadingTimeSlots = ref(false);

const disabledRequisicaoDate = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date.getTime() < today.getTime();
};

const onDateChange = async (value) => {
  newRequisicao.value.horaRequisicao = '';
  availableTimeSlots.value = [];

  if (value && newRequisicao.value.vagaId) {
    loadingTimeSlots.value = true;
    try {
      const formattedDate = formatDateBRA(value);
      const { data } = await apiClient.get('/requisicoes/available-times', {
        params: {
          vacancyId: newRequisicao.value.vagaId,
          date: formattedDate,
        },
      });
      availableTimeSlots.value = data;
    } catch (error) {
      ElMessage.error(getErrorMessage(error));
      console.error('Erro ao buscar horários disponíveis', error);
    } finally {
      loadingTimeSlots.value = false;
    }
  }
};

const confirmCreateRequisicao = async () => {
  if (
      !newRequisicao.value.nomeFalecido ||
      !newRequisicao.value.vagaId ||
      !newRequisicao.value.dataRequisicao ||
      !newRequisicao.value.horaRequisicao ||
      !rgFile.value ||
      !doFile.value
  ) {
    alert('Dados incompletos, favor verificar');
    return;
  }
  if (newRequisicao.value.obeso !== true && newRequisicao.value.obeso !== false) {
    alert('Informe se o falecido é obeso');
    return;
  }
  if (!newRequisicao.value.doId || !newRequisicao.value.doId.trim()) {
    alert('Informe o ID da DO (Documento de Óbito)');
    return;
  }
  if (!confirm('Confirma a criação da requisição?')) {
    return;
  }
  await createRequisicao();
};

const createRequisicao = async () => {
  if (!requisicaoFormRef.value) return;
  const formData = new FormData();
  formData.append('nomeFalecido', newRequisicao.value.nomeFalecido || '');
  formData.append('tipoDocumento', newRequisicao.value.tipoDocumento || '');
  formData.append('cpfFalecido', (newRequisicao.value.cpfFalecido || '').replace(/[.-]/g, ''));
  if (newRequisicao.value.dataFalecimento) {
    formData.append('dataFalecimento', formatDateBRA(newRequisicao.value.dataFalecimento));
  }
  formData.append('vagaId', newRequisicao.value.vagaId);
  if (newRequisicao.value.dataRequisicao) {
    formData.append('dataRequisicao', formatDateBRA(newRequisicao.value.dataRequisicao));
  }
  if (newRequisicao.value.horaRequisicao) {
    formData.append('horaRequisicao', newRequisicao.value.horaRequisicao);
  }
  formData.append('reason', newRequisicao.value.reason || '');
  formData.append('obeso', newRequisicao.value.obeso);
  formData.append('doId', newRequisicao.value.doId || '');
  formData.append('arquivos', rgFile.value.raw);
  formData.append('arquivos', doFile.value.raw);

  try {
    await apiClient.post('/requisicoes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    ElMessage.success('Requisição criada com sucesso!');
    Object.assign(newRequisicao.value, {
      nomeFalecido: '',
      tipoDocumento: '',
      cpfFalecido: '',
      dataFalecimento: null,
      vagaId: '',
      dataRequisicao: null,
      horaRequisicao: '',
      reason: '',
      obeso: null,
      doId: ''
    });
    clearFiles();
    fetchMinhasRequisicoes();
  } catch (error) {
    ElMessage.error(getErrorMessage(error));
    if (error.response && error.response.status === 409) {
      fetchVagasDisponiveis(); // Refresh the vacancy list
    }
    clearFiles();
  }
};

// #4 Funerária: comprovante de pagamento anexado fora do formulário.
const hasComprovante = (row) => {
  try {
    const files = row.caminhoArquivos ? JSON.parse(row.caminhoArquivos) : [];
    return !!files[2];
  } catch (e) {
    return false;
  }
};

const comprovanteDialogVisible = ref(false);
const comprovanteTargetId = ref(null);
const comprovanteSubmitting = ref(false);
const comprovanteFile = ref(null);
const comprovanteUploadRef = ref(null);
const extraFileList = ref([]);
const extraFilesUploadRef = ref(null);

const openComprovanteDialog = (row) => {
  comprovanteTargetId.value = row.id;
  comprovanteFile.value = null;
  extraFileList.value = [];
  if (comprovanteUploadRef.value) comprovanteUploadRef.value.clearFiles();
  if (extraFilesUploadRef.value) extraFilesUploadRef.value.clearFiles();
  comprovanteDialogVisible.value = true;
};

const onComprovanteChange = (file, fileListVal) => {
  if (fileListVal && fileListVal.length > 0) {
    comprovanteFile.value = fileListVal[0];
  }
};
const onComprovanteRemove = () => {
  comprovanteFile.value = null;
  if (comprovanteUploadRef.value) comprovanteUploadRef.value.clearFiles();
};


const handleExtraFilesExceed = () => {
  ElMessage.warning('Você pode anexar no máximo 4 arquivos extras.');
};

const submitComprovanteUpload = async () => {
  if (!comprovanteFile.value) {
    ElMessage.error('O comprovante de pagamento é obrigatório.');
    return;
  }
  comprovanteSubmitting.value = true;
  const fd = new FormData();
  fd.append('comprovante', comprovanteFile.value.raw);
  
  for (const f of extraFileList.value) {
    fd.append('arquivosExtras', f.raw);
  }
  
  try {
    await apiClient.post(`/requisicoes/${comprovanteTargetId.value}/comprovante`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    ElMessage.success('Comprovante e arquivos anexados com sucesso!');
    comprovanteDialogVisible.value = false;
    fetchMinhasRequisicoes();
  } catch (error) {
    ElMessage.error(error.response?.data?.message || 'Erro ao anexar comprovante.');
  } finally {
    comprovanteSubmitting.value = false;
  }
};

const extFromContentType = (ct) => {
  const map = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/webp': 'webp',
    'image/tiff': 'tiff',
    'image/svg+xml': 'svg',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.oasis.opendocument.text': 'odt',
    'text/plain': 'txt',
    'text/csv': 'csv',
  };
  const base = (ct || '').split(';')[0].trim().toLowerCase();
  return map[base] || 'bin';
};
const filenameFromResponse = (response, fallbackBase) => {
  const cd = response.headers['content-disposition'] || '';
  const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
  if (m && m[1]) {
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  }
  return `${fallbackBase}.${extFromContentType(response.headers['content-type'])}`;
};

const handleDownloadComprovante = async (requisicaoId) => {
  try {
    const response = await apiClient.get(
      `/requisicoes/download/${requisicaoId}/boleto`,
      { responseType: 'blob' }
    );
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filenameFromResponse(response, 'COMPROVANTE_DO'));
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    ElMessage.error(getErrorMessage(error));
  }
};

const minhasRequisicoes = ref([]);
const loadingMinhasRequisicoes = ref(false);
const fetchMinhasRequisicoes = async () => {
  loadingMinhasRequisicoes.value = true;
  try {
    const { data } = await apiClient.get('/requisicoes/funeraria');
    minhasRequisicoes.value = data;
  } catch (error) {
    ElMessage.error(getErrorMessage(error));
    console.error('Erro ao buscar requisições', error);
  } finally {
    loadingMinhasRequisicoes.value = false;
  }
};

const formatVaga = (row) => {
  if (row.vaga && row.vaga.quadra && row.vaga.numeroSepultura && row.vaga.tipo) {
    return `Quadra ${row.vaga.quadra} - Sep. ${row.vaga.numeroSepultura} - Tipo ${row.vaga.tipo}`;
  }
  return 'N/A';
};

const requisicoesAndamento = ref([]);
const loadingRequisicoesAndamento = ref(false);
const fetchRequisicoesAndamento = async () => {
  loadingRequisicoesAndamento.value = true;
  try {
    const { data } = await apiClient.get('/requisicoes/andamento');
    requisicoesAndamento.value = data;
  } catch (error) {
    ElMessage.error(getErrorMessage(error));
    console.error('Erro ao buscar requisições em andamento', error);
  } finally {
    loadingRequisicoesAndamento.value = false;
  }
};

const formatStatus = (row) => {
  return row.status === 'CANCELADO'
      ? 'Rejeitada, entrar em contato.'
      : row.status;
};

let vagasInterval;
let minhasInterval;
let andamentoInterval;

const vagasDisponiveisFilter = ref("");
const minhasRequisicoesFilter = ref("");
const requisicoesAndamentoFilter = ref("");

const filteredVagasDisponiveis = computed(() => {
  if (!vagasDisponiveisFilter.value) return vagasDisponiveis.value;
  return vagasDisponiveis.value.filter(vaga =>
    Object.values(vaga).join(' ').toLowerCase().includes(vagasDisponiveisFilter.value.toLowerCase())
  );
});
const filteredMinhasRequisicoes = computed(() => {
  if (!minhasRequisicoesFilter.value) return minhasRequisicoes.value;
  return minhasRequisicoes.value.filter(req =>
    Object.values(req).join(' ').toLowerCase().includes(minhasRequisicoesFilter.value.toLowerCase())
  );
});
const filteredRequisicoesAndamento = computed(() => {
  if (!requisicoesAndamentoFilter.value) return requisicoesAndamento.value;
  return requisicoesAndamento.value.filter(req =>
    Object.values(req).join(' ').toLowerCase().includes(requisicoesAndamentoFilter.value.toLowerCase())
  );
});

const greetingMessage = computed(() => {
  const now = new Date();
  const hour = now.getHours();
  let greeting = 'Bom dia';
  if (hour >= 18 || hour < 5) {
    greeting = 'Boa noite';
  } else if (hour >= 12) {
    greeting = 'Boa tarde';
  }
  const nome = userStore.user && userStore.user.nome ? userStore.user.nome : 'Usuário';
  const funerariaNome = userStore.user && userStore.user.funeraria && userStore.user.funeraria.nome ? userStore.user.funeraria.nome : 'Funerária';
  return `${greeting}, ${nome} (${funerariaNome})`;
});

const hasActiveReservation = computed(() => {
  return newRequisicao.value.vagaId && reservationTimer.value > 0;
});

watch(activeTab, (newTab, oldTab) => {
  // Impede acesso à aba "Criar Requisição" sem vaga selecionada
  if (newTab === 'criarRequisicao' && !hasActiveReservation.value) {
    ElMessage.warning('Selecione uma vaga disponível antes de criar uma requisição.');
    activeTab.value = oldTab || 'vagasDisponiveis';
    return;
  }

  if (newTab !== 'criarRequisicao' && reservationInterval) {
    clearInterval(reservationInterval);
    reservationTimer.value = 0;
  }
  if (newTab === 'vagasDisponiveis') fetchVagasDisponiveis();
  if (newTab === 'minhasRequisicoes') fetchMinhasRequisicoes();
  if (newTab === 'requisicoesAndamento') fetchRequisicoesAndamento();
});

onMounted(() => {
  if (activeTab.value === 'vagasDisponiveis') fetchVagasDisponiveis();
  if (activeTab.value === 'minhasRequisicoes') fetchMinhasRequisicoes();
  if (activeTab.value === 'requisicoesAndamento') fetchRequisicoesAndamento();

  // Auto-refresh disabled for serverless compatibility
  // vagasInterval = setInterval(() => {
  //   if (activeTab.value === 'vagasDisponiveis') fetchVagasDisponiveis();
  // }, 45000);
  // minhasInterval = setInterval(() => {
  //   if (activeTab.value === 'minhasRequisicoes') fetchMinhasRequisicoes();
  // }, 45000);
  // andamentoInterval = setInterval(() => {
  //   if (activeTab.value === 'requisicoesAndamento') fetchRequisicoesAndamento();
  // }, 45000);
});

const refreshCooldown = ref(0);
let refreshCooldownInterval = null;

const handleRefreshVagas = () => {
  if (refreshCooldown.value > 0 || loadingVagasDisponiveis.value) return;
  fetchVagasDisponiveis();
  refreshCooldown.value = 30;
  if (refreshCooldownInterval) clearInterval(refreshCooldownInterval);
  refreshCooldownInterval = setInterval(() => {
    if (refreshCooldown.value > 0) {
      refreshCooldown.value--;
    } else {
      clearInterval(refreshCooldownInterval);
    }
  }, 1000);
};

onUnmounted(() => {
  clearInterval(vagasInterval);
  clearInterval(minhasInterval);
  clearInterval(andamentoInterval);
  if (refreshCooldownInterval) clearInterval(refreshCooldownInterval);
});
</script>

<style scoped>
.funeraria-dashboard {
  padding: 12px 24px;
  box-sizing: border-box;
  width: 100%;
  max-width: none;
}
.tab-content {
  margin-top: 20px;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.upload-container {
  display: flex;
  align-items: center;
  gap: 20px;
}
.file-status {
  font-size: 14px;
  color: #555;
}
.reservation-timer {
  color: #e6a23c;
  font-size: 16px;
  margin-bottom: 12px;
}
.reservation-timer-expired {
  color: #f56c6c;
  font-size: 16px;
  margin-bottom: 12px;
}
.vagas-warning {
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  padding: 8px 16px;
  margin-bottom: 10px;
  color: #e6a23c;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 16px;
}

.no-reservation-message {
  margin-bottom: 20px;
}
</style>
